/**
 * A viewing room as a visitor sees it: the door, and what is behind it.
 *
 * The visitor may be anybody with the link, signed in or not, and the two
 * questions this file answers are "what must I give before this opens?" and
 * "what does the room show once it has?" Both are pure rules — the ladder of
 * prompts in `requiredFields` and `outstandingField`, and the presentation in
 * `resolveRoom` and below — so the controller in `useRoomController.ts` is
 * left holding only what was typed and what arrived. The words are in
 * `room.constants.ts`.
 *
 * The owner's side — creating the room and choosing its works — is
 * `viewingRooms/`, not here. The room borrows the work record's identity rows
 * so Artist / Year / Medium read the same on both, and structurally cannot
 * borrow its placement rows, because a `PublicWork` carries no collection.
 */

import type { PublicProvenanceEntry, PublicWork, RoomGateResponse, RoomResponse } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { HISTORY_KINDS, METHOD_EVENTS } from "../provenance/provenance.constants.ts";
import type { EventTone } from "../provenance/provenance.constants.ts";
import { attributionOf, identityRows } from "../work/work.model.ts";
import type { IdentityRow, WorkIdentityView } from "../work/work.model.ts";
import { HANDLE_PATTERN, ROOM_COPY, SLUG_PATTERN } from "./room.constants.ts";

/** The two halves of a room's address, once they look like halves of one. */
export interface RoomAddress {
  handle: string;
  slug: string;
}

/**
 * Null for anything that is not a room address.
 *
 * Both halves arrive from a URL anybody can type and are put straight into a
 * request path. Refusing here means no request is made at all for an address
 * that could not name a room — and the server answers unknown, hidden and
 * deleted identically anyway, so nothing is disclosed by refusing early.
 */
export function parseRoomAddress(
  handle: string | undefined,
  slug: string | undefined,
): RoomAddress | null {
  const h = (handle ?? "").trim().toLowerCase();
  const s = (slug ?? "").trim().toLowerCase();

  return HANDLE_PATTERN.test(h) && SLUG_PATTERN.test(s) ? { handle: h, slug: s } : null;
}

/** Whoever is at the door, as much as this page is allowed to know. */
export interface Viewer {
  signedIn: boolean;
  email: string | null;
}

/**
 * What the visitor still has to give before the room opens.
 *
 * One pure function, so the ladder is a rule rather than a sequence of ifs
 * spread across a controller. Both collapse for a signed-in member at an open
 * room, which is the stated requirement: they see no prompt at all.
 *
 * Being an ONDO member is NOT the password. A signed-in visitor at a locked
 * room still has to type it, and there is a test whose only job is that.
 */
export function requiredFields(
  cover: { requiresPassword: boolean },
  viewer: Viewer,
): ("email" | "password")[] {
  return [
    ...(viewer.signedIn ? [] : (["email"] as const)),
    ...(cover.requiresPassword ? (["password"] as const) : []),
  ];
}

/**
 * The next thing the visitor has to give, or null when nothing is left.
 *
 * `requiredFields` says what the room DEMANDS; this says what is still
 * OUTSTANDING, and the difference is the whole ladder. Reading only the first
 * required field is what produced the dead end this replaced: a room wanting an
 * email and a password showed the email box, submitted with no password, and
 * reported "That password is not right" on a screen with nowhere to type one.
 *
 * Null is the signal to actually open the room. A signed-in member at an open
 * room gets null on the first render, which is why they are never prompted.
 */
export function outstandingField(
  cover: { requiresPassword: boolean },
  viewer: Viewer,
  supplied: { email: string | null },
): "email" | "password" | null {
  const required = requiredFields(cover, viewer);

  if (required.includes("email") && supplied.email === null) return "email";
  // The password is never "supplied" locally: it is checked by being sent, so
  // it stays outstanding until the room opens or refuses.
  if (required.includes("password")) return "password";
  return null;
}

export interface RoomHead {
  title: string;
  by: string;
  subtitle: string | null;
  message: string | null;
  /** "12 works", or null while a password is outstanding. */
  count: string | null;
}

export interface RoomCardView {
  id: string;
  title: string;
  attribution: string;
  medium: string | null;
  /** The first photograph, or null when the work has none. */
  imageUrl: string | null;
  alt: string;
}

export interface ChainLinkView {
  key: string;
  /**
   * "Acquired", "Bequeathed", "Sold at auction".
   *
   * Off METHOD_EVENTS for a sale and off HISTORY_KINDS for an auction, so it
   * reads as it does on the work record.
   */
  action: string;
  tone: EventTone;
  glyph: string;
  /** "1927 – 1931", or "1998 to the present". */
  when: string | null;
  /** Null on an auction whose buyer the record does not name. */
  owner: string | null;
  /** The gallery and the place, joined. Null when neither was recorded. */
  where: string | null;
}

export interface RoomWorkView {
  identity: WorkIdentityView;
  images: { url: string; alt: string; selected: boolean }[];
  hero: { url: string; alt: string } | null;
  description: string | null;
  chain: ChainLinkView[] | null;
}

export type RoomView =
  | { kind: "loading"; message: string }
  /** A wrong address, a hidden room and a deleted one, in one answer. */
  | { kind: "missing"; title: string; message: string }
  | {
      kind: "needs-email";
      head: RoomHead;
      lead: string;
      label: string;
      action: string;
      value: string;
      problem: string | null;
      busy: boolean;
    }
  | {
      kind: "needs-password";
      head: RoomHead;
      lead: string;
      label: string;
      action: string;
      value: string;
      problem: string | null;
      busy: boolean;
    }
  /** Terminal. The door is shut, so there is nothing to retype. */
  | { kind: "refused"; head: RoomHead; title: string; message: string }
  | { kind: "showing"; head: RoomHead; cards: RoomCardView[]; note: string; footnote: string }
  | { kind: "work"; head: RoomHead; work: RoomWorkView; backLabel: string; footnote: string };

/**
 * The screen.
 *
 * Note the input: there is no `me` on it, and there must not be. `useMe` returns
 * `{ status: "loading" }` for ever when there is no user id, so a public screen
 * that branched on it would spin at exactly the people this page exists for.
 * The signature is the assertion.
 */
export function resolveRoom(input: {
  address: RoomAddress | null;
  cover: RoomGateResponse | null;
  coverFailed: boolean;
  contents: RoomResponse | null;
  viewer: Viewer;
  email: string;
  /** The address already accepted, which is what advances the ladder. */
  givenEmail: string | null;
  password: string;
  problem: string | null;
  /** Set when the room has stopped answering, rather than merely said no. */
  refused: boolean;
  busy: boolean;
  openWorkId: string | null;
}): RoomView {
  if (input.address === null || input.coverFailed) {
    return { kind: "missing", title: ROOM_COPY.missingTitle, message: ROOM_COPY.missing };
  }
  if (!input.cover) return { kind: "loading", message: ROOM_COPY.loading };

  const head = toRoomHead(input.cover);

  if (input.contents) {
    const full = toRoomHead(input.contents);
    const open = input.openWorkId
      ? input.contents.works.find((work) => work.id === input.openWorkId)
      : undefined;

    if (open) {
      return {
        kind: "work",
        head: full,
        work: toRoomWork(open, input.contents.showDimensions),
        backLabel: ROOM_COPY.back,
        footnote: ROOM_COPY.footnote,
      };
    }

    return {
      kind: "showing",
      head: full,
      cards: input.contents.works.map(toRoomCard),
      note: ROOM_COPY.visitRecorded,
      footnote: ROOM_COPY.footnote,
    };
  }

  if (input.refused) {
    return { kind: "refused", head, title: ROOM_COPY.refusedTitle, message: ROOM_COPY.refused };
  }

  const outstanding = outstandingField(input.cover, input.viewer, {
    email: input.givenEmail,
  });

  if (outstanding === "email") {
    return {
      kind: "needs-email",
      head,
      lead: ROOM_COPY.emailLead,
      label: ROOM_COPY.emailLabel,
      action: ROOM_COPY.emailAction,
      value: input.email,
      problem: input.problem,
      busy: input.busy,
    };
  }

  if (outstanding === "password") {
    return {
      kind: "needs-password",
      head,
      lead: ROOM_COPY.passwordLead,
      label: ROOM_COPY.passwordLabel,
      action: ROOM_COPY.passwordAction,
      value: input.password,
      problem: input.problem,
      busy: input.busy,
    };
  }

  // Nothing outstanding and nothing loaded: the entry call is in flight, which
  // is what a signed-in member at an open room sees for one tick.
  return { kind: "loading", message: ROOM_COPY.loading };
}

export function toRoomHead(
  source: Pick<RoomGateResponse, "title" | "ownerName" | "subtitle" | "message"> & {
    workCount?: number | null;
    works?: unknown[];
  },
): RoomHead {
  const count = source.works ? source.works.length : (source.workCount ?? null);

  return {
    title: source.title,
    by: `${ROOM_COPY.by} ${source.ownerName}`,
    subtitle: source.subtitle,
    message: source.message,
    count: count === null ? null : `${String(count)} ${count === 1 ? "work" : "works"}`,
  };
}

export function toRoomCard(work: PublicWork): RoomCardView {
  return {
    id: work.id,
    title: work.title,
    attribution: attributionOf(work),
    medium: work.medium,
    imageUrl: work.imageUrls[0] ?? null,
    alt: `${work.title} — ${ROOM_COPY.imageOf}`,
  };
}

/**
 * One work, as a room may present it.
 *
 * `identityRows` is shared with the work record, so Artist / Year / Medium read
 * identically on both. `placementRows` is not called and CANNOT be: it takes a
 * `PlacedWork`, and a `PublicWork` carries no collection. The withholding is
 * structural rather than a rule somebody has to remember.
 */
export function toRoomWork(work: PublicWork, showDimensions: boolean): RoomWorkView {
  return {
    identity: {
      title: work.title,
      attribution: attributionOf(work),
      // No signature count. A public mark on a stranger's chain of title is its
      // own disclosure decision — see accessFGARules, "Public attestation
      // counts" — so a room shows none, in either direction.
      badge: null,
      rows: roomIdentityRows(work, showDimensions),
    },
    images: work.imageUrls.map((url, index) => ({
      url,
      alt: `${work.title} — ${ROOM_COPY.imageOf} ${String(index + 1)}`,
      selected: index === 0,
    })),
    hero:
      work.imageUrls[0] === undefined
        ? null
        : { url: work.imageUrls[0], alt: `${work.title} — ${ROOM_COPY.imageOf}` },
    description: work.description,
    chain: work.provenance === null ? null : work.provenance.map(toChainLink),
  };
}

/** The catalogue rows a room shows, and never a placement one. */
export function roomIdentityRows(work: PublicWork, showDimensions: boolean): IdentityRow[] {
  return identityRows(
    {
      attributedName: work.attributedName,
      year: work.year,
      medium: work.medium,
      dimensions: work.dimensions,
      editionText: work.editionText,
      editionSize: work.editionSize,
    },
    // The server already omits the values; this decides whether the rows appear
    // at all. An empty Dimensions cell reads as a gap in the record rather than
    // as a room declining to say.
    { measurements: showDimensions },
  );
}

/**
 * A room shows the chain of title, and only `sale` and `auction` reach it.
 *
 * A sale reads its verb off the METHOD — 'Received as a gift', 'Bequeathed' —
 * for the reason the work record does: the kind says title moved and the method
 * says how, and reading the verb off the kind would show a family's picture as
 * bought. An auction has no method, so it reads off its own kind.
 *
 * A room may honestly not know who bought at auction; `owner` is null there and
 * the room says the sale without asserting a holder.
 */
export function toChainLink(entry: PublicProvenanceEntry): ChainLinkView {
  const event = entry.method === null ? null : METHOD_EVENTS[entry.method];
  const kind = HISTORY_KINDS[entry.kind];
  const where = [entry.galleryText, entry.locationText].filter(Boolean).join(", ");

  return {
    key: String(entry.position),
    action: event?.action ?? kind.action,
    tone: event?.tone ?? kind.tone,
    glyph: event?.glyph ?? kind.glyph,
    when: describeSpan(entry),
    owner: entry.ownerName,
    where: where === "" ? null : where,
  };
}

/**
 * '1927 – 1931', or '1998 to the present'.
 *
 * A link with no end is open-ended, and the only one a room ever shows without
 * an end date is the present owner — which the room's own toggle admitted.
 */
function describeSpan(entry: PublicProvenanceEntry): string | null {
  if (entry.startText === null && entry.endText === null) return null;
  if (entry.endText === null) return `${entry.startText ?? ""} ${ROOM_COPY.chainPresent}`.trim();
  if (entry.startText === null) return `until ${entry.endText}`;
  return `${entry.startText} – ${entry.endText}`;
}

/**
 * Why entering failed, in the visitor's words.
 *
 * A 404 is folded into `missing` by the caller. Everything else prefers the
 * API's own message, which for a wrong password is the sentence a person needs.
 */
export function describeEntryError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message.trim() === "" ? ROOM_COPY.unknownError : error.message;
  }
  return ROOM_COPY.unknownError;
}

/**
 * The loosest thing worth calling an address.
 *
 * Deliberately not a strict RFC check, and the same expression the API applies:
 * nothing is sent to this address and nothing verifies it — it is recorded as a
 * claim — so a validator strict enough to reject a real address would be
 * refusing entry over a field that proves nothing. What it catches is an empty
 * box and an obvious non-answer, which is all it is for.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** True for the status that means "stop offering the field". */
export function isTerminal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 429;
}

/** True for the status that means the room is not there, whatever the reason. */
export function isMissing(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
